import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  message,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CheckCircleFilled,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ReloadOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import {
  CancelarInscripcion,
  GetInscriptos,
  InscripcionColeccion,
  Inscripto,
  OtorgarPuntosInscripto,
  UsuarioNoEncontradoError,
  XpYaOtorgadaError,
} from "../inscripciones";

// ══════════════════════════════════════════════════════════════
// Lista de inscriptos para el panel de Admin (agregado 11/10/2026)
//
// Se abre al tocar un evento o una noticia en las tablas de Admin.
// Permite copiar los emails, descargar un CSV (se abre en Excel) y
// dar de baja a alguien.
//
// 04/11/2026: columna "XP" con un botón por inscripto que le acredita
// la XP del evento y le manda una notificación. Solo aparece si el
// evento/noticia tiene XP cargada. Ver OtorgarPuntosInscripto().
// ══════════════════════════════════════════════════════════════

export interface InscriptosTarget {
  coleccion: InscripcionColeccion;
  id: string;
  titulo: string;
  /** Si el botón "Inscribirme" está activado en ese evento/noticia. */
  abierta: boolean;
  /** XP que da el evento/noticia (0 = no da XP, no se muestra el botón). */
  exp: number;
}

interface InscriptosModalProps {
  target: InscriptosTarget | null;
  onClose: () => void;
}

const formatFecha = (fecha: Date | null) =>
  fecha ? dayjs(fecha).format("DD/MM/YYYY HH:mm") : "-";

const csvCell = (value: string) => `"${value.replace(/"/g, '""')}"`;

const InscriptosModal: React.FC<InscriptosModalProps> = ({
  target,
  onClose,
}) => {
  const [inscriptos, setInscriptos] = useState<Inscripto[]>([]);
  const [loading, setLoading] = useState(false);
  // Uid del inscripto al que se le está dando XP (para el spinner del botón).
  const [grantingUid, setGrantingUid] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!target) return;
    setLoading(true);
    try {
      setInscriptos(await GetInscriptos(target.coleccion, target.id));
    } catch {
      message.error("No se pudo cargar la lista de inscriptos.");
    } finally {
      setLoading(false);
    }
  }, [target]);

  useEffect(() => {
    if (target) {
      void load();
    } else {
      setInscriptos([]);
    }
  }, [target, load]);

  const tipo = target?.coleccion === "Eventos" ? "evento" : "noticia";
  const exp = target?.exp ?? 0;

  const handleCopyEmails = async () => {
    const emails = inscriptos.map((i) => i.Email).filter(Boolean);
    try {
      await navigator.clipboard.writeText(emails.join(", "));
      message.success(`Copiaste ${emails.length} emails.`);
    } catch {
      message.error("No se pudieron copiar los emails.");
    }
  };

  const handleDownloadCsv = () => {
    if (!target) return;
    const rows = [
      ["Nombre", "Email", "Fecha de inscripción", "XP otorgada"],
      ...inscriptos.map((i) => [
        i.Nombre,
        i.Email,
        formatFecha(i.Fecha),
        i.PuntosOtorgados !== null ? String(i.PuntosOtorgados) : "",
      ]),
    ];
    // ";" y BOM para que Excel en español lo abra con columnas y acentos.
    const csv =
      "\uFEFF" + rows.map((row) => row.map(csvCell).join(";")).join("\r\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" }),
    );
    const slug =
      target.titulo
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 40) || target.id;
    const link = document.createElement("a");
    link.href = url;
    link.download = `inscriptos-${slug}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleRemove = async (record: Inscripto) => {
    if (!target) return;
    try {
      await CancelarInscripcion(target.coleccion, target.id, record.Uid);
      message.success(`${record.Nombre || record.Email} ya no está inscripto.`);
      void load();
    } catch {
      message.error("No se pudo quitar la inscripción.");
    }
  };

  const handleGrantXp = async (record: Inscripto) => {
    if (!target || !exp || grantingUid) return;
    const nombre = record.Nombre || record.Email || "El usuario";
    setGrantingUid(record.Uid);
    try {
      const { nuevoNivel } = await OtorgarPuntosInscripto({
        coleccion: target.coleccion,
        itemId: target.id,
        titulo: target.titulo,
        uid: record.Uid,
        puntos: exp,
      });
      message.success(`${nombre} recibió ${exp} XP (nivel ${nuevoNivel}).`);
      // Actualizamos la fila sin recargar toda la lista.
      setInscriptos((prev) =>
        prev.map((i) =>
          i.Uid === record.Uid
            ? { ...i, PuntosOtorgados: exp, PuntosOtorgadosEl: new Date() }
            : i,
        ),
      );
    } catch (error) {
      if (error instanceof XpYaOtorgadaError) {
        message.warning(`${nombre} ya había recibido la XP de esta ${tipo}.`);
        void load();
      } else if (error instanceof UsuarioNoEncontradoError) {
        message.error(`${nombre} ya no tiene cuenta en la app.`);
      } else {
        message.error("No se pudo otorgar la XP. Revisá la conexión y probá de nuevo.");
      }
    } finally {
      setGrantingUid(null);
    }
  };

  const xpColumn = {
    title: "XP",
    key: "xp",
    width: 140,
    render: (_: unknown, record: Inscripto) =>
      record.PuntosOtorgados !== null ? (
        <Tooltip
          title={`Otorgada el ${formatFecha(record.PuntosOtorgadosEl)}`}
        >
          <Tag color="green" icon={<CheckCircleFilled />}>
            +{record.PuntosOtorgados} otorgada
          </Tag>
        </Tooltip>
      ) : (
        <Button
          size="small"
          type="primary"
          icon={<ThunderboltOutlined />}
          loading={grantingUid === record.Uid}
          disabled={Boolean(grantingUid) && grantingUid !== record.Uid}
          onClick={() => void handleGrantXp(record)}
        >
          Dar +{exp} XP
        </Button>
      ),
  };

  const columns = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_: unknown, __: Inscripto, index: number) => index + 1,
    },
    {
      title: "Nombre",
      dataIndex: "Nombre",
      key: "Nombre",
      render: (value: string) => value || "-",
    },
    { title: "Email", dataIndex: "Email", key: "Email" },
    {
      title: "Inscripción",
      dataIndex: "Fecha",
      key: "Fecha",
      render: (value: Date | null) => formatFecha(value),
    },
    // Solo si el evento/noticia da XP.
    ...(exp > 0 ? [xpColumn] : []),
    {
      title: "",
      key: "actions",
      width: 60,
      render: (_: unknown, record: Inscripto) => (
        <Popconfirm
          title="¿Quitar a esta persona de la lista?"
          okText="Quitar"
          okButtonProps={{ danger: true }}
          cancelText="Cancelar"
          onConfirm={() => void handleRemove(record)}
        >
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    },
  ];

  const otorgados = inscriptos.filter((i) => i.PuntosOtorgados !== null).length;

  return (
    <Modal
      open={Boolean(target)}
      onCancel={onClose}
      width={860}
      title={
        <Space wrap>
          <span>Inscriptos</span>
          <Tag color="purple">{inscriptos.length}</Tag>
        </Space>
      }
      footer={[
        <Button key="cerrar" type="primary" onClick={onClose}>
          Cerrar
        </Button>,
      ]}
    >
      {target ? (
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Typography.Text strong style={{ color: "var(--dashboard-header-blue)" }}>
            {target.titulo}
          </Typography.Text>

          {!target.abierta ? (
            <Alert
              type="info"
              showIcon
              message={`El botón "Inscribirme" está desactivado en esta ${tipo}. Activalo desde "Editar" para que la gente pueda anotarse.`}
            />
          ) : null}

          {exp > 0 ? (
            <Typography.Text type="secondary">
              Esta {tipo} da {exp} XP. Con «Dar +{exp} XP» se la sumás al
              usuario y le llega una notificación. XP ya otorgada: {otorgados}{" "}
              de {inscriptos.length}.
            </Typography.Text>
          ) : null}

          <Space wrap>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => void load()}
              loading={loading}
            >
              Actualizar
            </Button>
            <Button
              icon={<CopyOutlined />}
              onClick={() => void handleCopyEmails()}
              disabled={!inscriptos.length}
            >
              Copiar emails
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadCsv}
              disabled={!inscriptos.length}
            >
              Descargar CSV
            </Button>
          </Space>

          <Table
            rowKey="Uid"
            size="small"
            columns={columns as any}
            dataSource={inscriptos}
            loading={loading}
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            scroll={{ x: true }}
            locale={{ emptyText: `Todavía no se inscribió nadie en esta ${tipo}.` }}
          />
        </Space>
      ) : null}
    </Modal>
  );
};

export default InscriptosModal;
